import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Plus, Pencil, Trash2, Wrench, TrendingUp, Clock, CheckCircle2 } from 'lucide-react';

const RM_TYPES = [
  { value: 'repair', label: 'Repair' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'tyre', label: 'Tyre' },
  { value: 'service', label: 'Service' },
  { value: 'body_work', label: 'Body Work' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'completed', label: 'Completed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pending', label: 'Pending' },
];

const RepairMaintenance = () => {
  const { API } = useAuth();
  const [records, setRecords] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterVehicle, setFilterVehicle] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [summary, setSummary] = useState({ total_cost: 0, total_count: 0, by_type: [] });

  const [form, setForm] = useState({
    vehicle_id: '',
    rm_type: 'repair',
    description: '',
    cost: '',
    vendor_name: '',
    rm_date: new Date().toISOString().split('T')[0],
    status: 'completed',
  });

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterVehicle !== 'all') params.vehicle_id = filterVehicle;
      if (filterType !== 'all') params.rm_type = filterType;

      const [recordsRes, vehiclesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/repair-maintenance`, { params }),
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/repair-maintenance/summary`),
      ]);
      setRecords(recordsRes.data);
      setVehicles(vehiclesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to fetch R&M data');
    } finally {
      setLoading(false);
    }
  }, [API, filterVehicle, filterType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({
      vehicle_id: '',
      rm_type: 'repair',
      description: '',
      cost: '',
      vendor_name: '',
      rm_date: new Date().toISOString().split('T')[0],
      status: 'completed',
    });
    setEditingRecord(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (record) => {
    setEditingRecord(record);
    setForm({
      vehicle_id: record.vehicle_id,
      rm_type: record.rm_type,
      description: record.description,
      cost: String(record.cost),
      vendor_name: record.vendor_name || '',
      rm_date: record.rm_date,
      status: record.status,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.vehicle_id || !form.description || !form.cost || !form.rm_date) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = { ...form, cost: parseFloat(form.cost) };

      if (editingRecord) {
        await axios.patch(`${API}/repair-maintenance/${editingRecord.id}`, payload);
        toast.success('Record updated');
      } else {
        await axios.post(`${API}/repair-maintenance`, payload);
        toast.success('R&M record created');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save record');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this R&M record?')) return;
    try {
      await axios.delete(`${API}/repair-maintenance/${id}`);
      toast.success('Record deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete record');
    }
  };

  const getVehicleLabel = (vehicleId) => {
    const v = vehicles.find((veh) => veh.id === vehicleId);
    return v ? `${v.vehicle_number} (${v.make} ${v.model})` : 'Unknown';
  };

  const getTypeLabel = (value) => {
    const type = RM_TYPES.find((t) => t.value === value);
    return type ? type.label : value;
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      in_progress: 'bg-blue-50 text-blue-700 border border-blue-200',
      pending: 'bg-amber-50 text-amber-700 border border-amber-200',
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-700'}`} data-testid={`rm-status-badge-${status}`}>
        {status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
        {status === 'in_progress' && <Clock className="w-3 h-3" />}
        {status === 'pending' && <Clock className="w-3 h-3" />}
        {status === 'in_progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      repair: 'bg-red-50 text-red-700',
      maintenance: 'bg-blue-50 text-blue-700',
      tyre: 'bg-orange-50 text-orange-700',
      service: 'bg-purple-50 text-purple-700',
      body_work: 'bg-pink-50 text-pink-700',
      electrical: 'bg-yellow-50 text-yellow-700',
      other: 'bg-slate-100 text-slate-700',
    };
    return colors[type] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6" data-testid="repair-maintenance-section">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <Wrench className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Total R&M Cost</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-rm-cost">
                  {summary.total_cost.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Total Records</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-rm-records">{summary.total_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {summary.by_type.slice(0, 2).map((item) => (
          <Card key={item.rm_type} className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200">
            <CardContent className="pt-6">
              <div>
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">{getTypeLabel(item.rm_type)}</p>
                <p className="text-2xl font-bold text-slate-900">
                  {item.total.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-slate-400">{item.count} entries</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Table Card */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <CardTitle className="text-lg font-semibold text-slate-900">Repair & Maintenance Records</CardTitle>
            <Button onClick={openCreateDialog} data-testid="add-rm-btn" className="bg-slate-900 text-white hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Add R&M Record
            </Button>
          </div>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 pt-2">
            <Select value={filterVehicle} onValueChange={setFilterVehicle}>
              <SelectTrigger className="w-[220px]" data-testid="filter-vehicle">
                <SelectValue placeholder="Filter by Vehicle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vehicles</SelectItem>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.vehicle_number} ({v.make} {v.model})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]" data-testid="filter-rm-type">
                <SelectValue placeholder="Filter by Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {RM_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="rm-table">
                <TableHeader>
                  <TableRow className="bg-slate-50/80">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">R&M ID</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vehicle</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Description</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cost</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vendor</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((record) => (
                    <TableRow key={record.id} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100">
                      <TableCell className="font-mono text-sm text-slate-600" data-testid={`rm-id-${record.id}`}>{record.rm_id}</TableCell>
                      <TableCell className="font-medium text-sm">{getVehicleLabel(record.vehicle_id)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getTypeBadgeColor(record.rm_type)}`}>
                          {getTypeLabel(record.rm_type)}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-[200px] truncate">{record.description}</TableCell>
                      <TableCell className="font-semibold text-sm">
                        {parseFloat(record.cost).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{record.vendor_name || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{record.rm_date}</TableCell>
                      <TableCell>{getStatusBadge(record.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => openEditDialog(record)} data-testid={`edit-rm-${record.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(record.id)} data-testid={`delete-rm-${record.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {records.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12 text-slate-400">
                        <Wrench className="w-8 h-8 mx-auto mb-2 opacity-40" />
                        <p className="text-sm">No R&M records found</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="sm:max-w-lg" data-testid="rm-dialog">
          <DialogHeader>
            <DialogTitle>{editingRecord ? 'Edit R&M Record' : 'New R&M Record'}</DialogTitle>
            <DialogDescription>Enter repair and maintenance details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Vehicle <span className="text-red-500">*</span></Label>
              <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })} disabled={!!editingRecord}>
                <SelectTrigger data-testid="form-vehicle-select">
                  <SelectValue placeholder="Select vehicle..." />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.vehicle_number} ({v.make} {v.model})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Type <span className="text-red-500">*</span></Label>
                <Select value={form.rm_type} onValueChange={(v) => setForm({ ...form, rm_type: v })}>
                  <SelectTrigger data-testid="form-rm-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RM_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="form-rm-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Cost (INR) <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.cost}
                  onChange={(e) => setForm({ ...form, cost: e.target.value })}
                  data-testid="form-rm-cost"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Date <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={form.rm_date}
                  onChange={(e) => setForm({ ...form, rm_date: e.target.value })}
                  data-testid="form-rm-date"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Vendor Name</Label>
              <Input
                placeholder="Vendor / Workshop name"
                value={form.vendor_name}
                onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                data-testid="form-vendor-name"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Description <span className="text-red-500">*</span></Label>
              <Textarea
                placeholder="Describe the work done..."
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="form-rm-description"
              />
            </div>

            <Button
              className="w-full bg-slate-900 text-white hover:bg-slate-800"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="submit-rm-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editingRecord ? 'Update Record' : 'Create Record'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RepairMaintenance;
